import { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  TextInput,
  FlatList,
  Image,
} from "react-native";
import Slider from "@react-native-community/slider";
import * as ImagePicker from "expo-image-picker";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter, useLocalSearchParams } from "expo-router";
import { getDog, updateDog, deleteDog, getRuns } from "../../lib/api";
import { Input } from "../../../components/Input";
import { Button } from "../../../components/Button";
import { AKC_BREEDS } from "../../lib/breeds";

export default function EditDogScreen() {
  const router = useRouter();
  const { dogId } = useLocalSearchParams<{ dogId: string }>();
  const [name, setName] = useState("");
  const [breed, setBreed] = useState("");
  const [breedQuery, setBreedQuery] = useState("");
  const [showBreedList, setShowBreedList] = useState(false);
  const [selectedHandicap, setSelectedHandicap] = useState("");
  const [sex, setSex] = useState("");
  const [registeredName, setRegisteredName] = useState("");
  const [akcNumber, setAkcNumber] = useState("");
  const [historicalPoints, setHistoricalPoints] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialTiktok, setSocialTiktok] = useState("");
  const [socialX, setSocialX] = useState("");
  const [socialYoutube, setSocialYoutube] = useState("");
  const [totalPoints, setTotalPoints] = useState("");
  const [runPoints, setRunPoints] = useState(0);
  const [pointsWarning, setPointsWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDog, setLoadingDog] = useState(true);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState<string | null>(null);
  const [photoFocusY, setPhotoFocusY] = useState(50);

  async function handlePickPhoto() {
    try {
      if (Platform.OS === "web") {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async (e: any) => {
          const file = e.target.files?.[0];
          if (!file) return;
          // Resize on web with canvas — keeps payload small.
          const reader = new FileReader();
          reader.onload = (ev) => {
            const img = new window.Image();
            img.onload = () => {
              const max = 600; // square-ish trading card so 600 is plenty
              const canvas = document.createElement("canvas");
              let w = img.width;
              let h = img.height;
              if (w > h) { h = (h / w) * max; w = max; } else { w = (w / h) * max; h = max; }
              canvas.width = w; canvas.height = h;
              canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
              const uri = canvas.toDataURL("image/jpeg", 0.85);
              setPhoto(uri);
              setPhotoFocusY(50); // reset focus to center on new upload
            };
            img.src = ev.target?.result as string;
          };
          reader.readAsDataURL(file);
        };
        input.click();
      } else {
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ["images"],
          allowsEditing: true,
          aspect: [3, 4],
          quality: 0.6,
          base64: true,
        });
        if (!result.canceled && result.assets[0]?.base64) {
          setPhoto(`data:image/jpeg;base64,${result.assets[0].base64}`);
          setPhotoFocusY(50);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to pick photo");
    }
  }

  useEffect(() => {
    if (!dogId) return;
    Promise.all([getDog(dogId), getRuns(dogId).catch(() => [])])
      .then(([dog, runs]) => {
        const runPts = (runs || []).reduce((sum, r) => sum + (parseFloat(r.points) || 0), 0);
        setRunPoints(runPts);
        setName(dog.name || "");
        setBreed(dog.breed || "");
        setRegisteredName(dog.registeredName || "");
        setAkcNumber(dog.akcRegistrationNumber || "");
        const s2 = (dog as any).social || {};
        setSocialInstagram(s2.instagram || "");
        setSocialFacebook(s2.facebook || "");
        setSocialTiktok(s2.tiktok || "");
        setSocialX(s2.x || "");
        setSocialYoutube(s2.youtube || "");
        setPhoto(dog.photo || null);
        setPhotoFocusY(typeof dog.photoFocusY === "number" ? dog.photoFocusY : 50);
        const hist = dog.historicalPoints && parseFloat(dog.historicalPoints) > 0 ? parseFloat(dog.historicalPoints) : 0;
        setHistoricalPoints(hist > 0 ? String(hist) : "");
        setTotalPoints(String(+(hist + runPts).toFixed(2)));
        // Normalize legacy values ("Bitch"/"Dog"/"M"/"F") to Male/Female
        const s = (dog.sex || "").toLowerCase();
        if (s.startsWith("f") || s === "bitch") setSex("Female");
        else if (s.startsWith("m") || s === "dog") setSex("Male");
        else setSex("");
        // Map handicap value to picker
        if (dog.handicap) {
          const h = parseFloat(dog.handicap);
          if (h >= 2.0) setSelectedHandicap("2.0");
          else if (h >= 1.5) setSelectedHandicap("1.5");
          else setSelectedHandicap("1.0");
        }
      })
      .catch((err) => setError(err.message || "Failed to load dog"))
      .finally(() => setLoadingDog(false));
  }, [dogId]);

  const filteredBreeds = useMemo(() => {
    if (!breedQuery.trim()) return AKC_BREEDS;
    const q = breedQuery.toLowerCase();
    return AKC_BREEDS.filter((b) => b.toLowerCase().includes(q));
  }, [breedQuery]);

  const handicapToHeight: Record<string, number> = { "2.0": 10, "1.5": 15, "1.0": 22 };

  function onHistoricalChange(t: string) {
    setHistoricalPoints(t);
    const hist = parseFloat(t) || 0;
    setTotalPoints(String(+(hist + runPoints).toFixed(2)));
    setPointsWarning("");
  }

  function onTotalChange(t: string) {
    setTotalPoints(t);
    const total = parseFloat(t) || 0;
    const newHist = total - runPoints;
    if (newHist < 0) {
      setPointsWarning(`That would put historical points below 0 (you already have ${runPoints.toFixed(2)} pts from runs). Historical will be clamped to 0.`);
      setHistoricalPoints("0");
    } else {
      setPointsWarning("");
      setHistoricalPoints(String(+newHist.toFixed(2)));
    }
  }

  async function handleSubmit() {
    if (!name.trim() || !breed.trim() || !selectedHandicap || !sex) {
      setError("Name, breed, sex, and handicap are required");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await updateDog(dogId!, {
        name: name.trim(),
        breed: breed.trim(),
        heightInches: String(handicapToHeight[selectedHandicap]),
        sex,
        registeredName: registeredName.trim() || null,
        akcRegistrationNumber: akcNumber.trim() || null,
        historicalPoints: historicalPoints.trim() ? Number(historicalPoints) : 0,
        social: {
          instagram: socialInstagram.trim(),
          facebook: socialFacebook.trim(),
          tiktok: socialTiktok.trim(),
          x: socialX.trim(),
          youtube: socialYoutube.trim(),
        },
      } as any);
      router.replace(`/(tabs)/dogs/${dogId}` as any);
    } catch (err: any) {
      setError(err.message || "Failed to update dog");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (Platform.OS === "web") {
      if (!confirm("Delete this dog and all their runs? This cannot be undone.")) return;
    }
    setLoading(true);
    try {
      await deleteDog(dogId!);
      router.replace("/(tabs)/dogs" as any);
    } catch (err: any) {
      setError(err.message || "Failed to delete dog");
      setLoading(false);
    }
  }

  if (loadingDog) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center" }}>
        <Text style={{ color: "#6b7280" }}>Loading...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
      >
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 16,
            paddingVertical: 14,
            backgroundColor: "#ffffff",
            borderBottomWidth: 1,
            borderBottomColor: "#e5e7eb",
          }}
        >
          <TouchableOpacity onPress={() => router.replace(`/(tabs)/dogs/${dogId}` as any)} style={{ marginRight: 12 }}>
            <Text style={{ color: "#1B2A4A", fontSize: 16 }}>← Back</Text>
          </TouchableOpacity>
          <Text style={{ fontSize: 18, fontWeight: "700", color: "#1B2A4A" }}>Edit Dog</Text>
        </View>

        <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }}>
          {error ? (
            <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
            </View>
          ) : null}


          <Input
            label="Dog's Call Name *"
            value={name}
            onChangeText={setName}
            placeholder="e.g. Bella"
            autoCapitalize="words"
          />
          {/* Breed searchable dropdown */}
          <View style={{ marginBottom: 16, zIndex: 10 }}>
            <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 6 }}>Breed *</Text>
            <TouchableOpacity
              onPress={() => setShowBreedList(!showBreedList)}
              style={{
                borderWidth: 1,
                borderColor: showBreedList ? "#1B2A4A" : "#d1d5db",
                borderRadius: 10,
                paddingVertical: 12,
                paddingHorizontal: 16,
                backgroundColor: "#fff",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text style={{ fontSize: 16, color: breed ? "#111827" : "#9ca3af" }}>
                {breed || "Select breed..."}
              </Text>
              <Text style={{ color: "#6b7280" }}>{showBreedList ? "▲" : "▼"}</Text>
            </TouchableOpacity>

            {showBreedList && (
              <View style={{
                borderWidth: 1,
                borderColor: "#d1d5db",
                borderRadius: 10,
                backgroundColor: "#fff",
                marginTop: 4,
                maxHeight: 250,
                overflow: "hidden",
              }}>
                <TextInput
                  value={breedQuery}
                  onChangeText={setBreedQuery}
                  placeholder="Search breeds..."
                  autoFocus
                  style={{
                    borderBottomWidth: 1,
                    borderBottomColor: "#e5e7eb",
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    fontSize: 15,
                    color: "#111827",
                  }}
                  placeholderTextColor="#9ca3af"
                />
                <ScrollView
                  style={{ maxHeight: 200 }}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                >
                  {filteredBreeds.map((item) => (
                    <TouchableOpacity
                      key={item}
                      onPress={() => {
                        setBreed(item);
                        setBreedQuery("");
                        setShowBreedList(false);
                      }}
                      style={{
                        paddingVertical: 11,
                        paddingHorizontal: 14,
                        backgroundColor: item === breed ? "#F0F2F5" : "#fff",
                        borderBottomWidth: 1,
                        borderBottomColor: "#f3f4f6",
                      }}
                    >
                      <Text style={{ fontSize: 15, color: item === breed ? "#1B2A4A" : "#111827", fontWeight: item === breed ? "600" : "400" }}>{item}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
          {/* Handicap picker */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 8 }}>Handicap / Dog Height *</Text>
            <View style={{ flexDirection: "row", gap: 10 }}>
              {[
                { handicap: "2.0", label: "2.0×", desc: "Under 12\"" },
                { handicap: "1.5", label: "1.5×", desc: "12\" – 17.99\"" },
                { handicap: "1.0", label: "1.0×", desc: "18\" and over" },
              ].map((opt) => {
                const selected = selectedHandicap === opt.handicap;
                return (
                  <TouchableOpacity
                    key={opt.handicap}
                    onPress={() => setSelectedHandicap(opt.handicap)}
                    style={{
                      flex: 1,
                      paddingVertical: 14,
                      paddingHorizontal: 8,
                      borderRadius: 10,
                      borderWidth: 2,
                      borderColor: selected ? "#1B2A4A" : "#d1d5db",
                      backgroundColor: selected ? "#F0F2F5" : "#fff",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ fontSize: 22, fontWeight: "800", color: selected ? "#1B2A4A" : "#374151" }}>
                      {opt.label}
                    </Text>
                    <Text style={{ fontSize: 11, color: selected ? "#1B2A4A" : "#6b7280", marginTop: 4, textAlign: "center" }}>
                      {opt.desc}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Sex picker — required, two options */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 8 }}>Sex *</Text>
            <View style={{ flexDirection: "row", gap: 10 }}>
              {[
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
              ].map((opt) => {
                const selected = sex === opt.value;
                return (
                  <TouchableOpacity
                    key={opt.value}
                    onPress={() => setSex(opt.value)}
                    style={{
                      flex: 1,
                      paddingVertical: 14,
                      borderRadius: 10,
                      borderWidth: 2,
                      borderColor: selected ? "#1B2A4A" : "#d1d5db",
                      backgroundColor: selected ? "#F0F2F5" : "#fff",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ fontSize: 16, fontWeight: "700", color: selected ? "#1B2A4A" : "#374151" }}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
          <Input
            label="AKC Registered Name (optional)"
            value={registeredName}
            onChangeText={setRegisteredName}
            placeholder="e.g. CH Bella's Blue Dream"
            autoCapitalize="words"
          />
          <Input
            label="AKC Registration # (optional)"
            value={akcNumber}
            onChangeText={setAkcNumber}
            placeholder="e.g. SR12345678"
            autoCapitalize="characters"
          />

          {/* Social handles — shown on the public profile only */}
          <View style={{ marginTop: 4, marginBottom: 8, padding: 12, borderRadius: 10, backgroundColor: "#F8F9FA", borderWidth: 1, borderColor: "#e5e7eb" }}>
            <Text style={{ fontSize: 13, fontWeight: "700", color: "#1B2A4A", marginBottom: 2 }}>
              Follow Links (public profile only)
            </Text>
            <Text style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
              Add your dog's social handles or full URLs — shown on the shareable public profile.
            </Text>
            <Input
              label="Instagram"
              value={socialInstagram}
              onChangeText={setSocialInstagram}
              placeholder="@username or full URL"
              autoCapitalize="none"
            />
            <Input
              label="Facebook"
              value={socialFacebook}
              onChangeText={setSocialFacebook}
              placeholder="page name or full URL"
              autoCapitalize="none"
            />
            <Input
              label="TikTok"
              value={socialTiktok}
              onChangeText={setSocialTiktok}
              placeholder="@username or full URL"
              autoCapitalize="none"
            />
            <Input
              label="X / Twitter"
              value={socialX}
              onChangeText={setSocialX}
              placeholder="@username or full URL"
              autoCapitalize="none"
            />
            <Input
              label="YouTube"
              value={socialYoutube}
              onChangeText={setSocialYoutube}
              placeholder="@channel or full URL"
              autoCapitalize="none"
            />
          </View>

          {/* Section intro — explains when to use historical/total points */}
          <View
            style={{
              backgroundColor: "#EFF6FF",
              borderLeftWidth: 3,
              borderLeftColor: "#1B2A4A",
              padding: 10,
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            <Text style={{ fontSize: 12, color: "#1B2A4A", lineHeight: 17 }}>
              Use this section if you haven't logged every run in Dash-Hound but know your dog's total AKC Fast CAT points. We'll fill in the gap so titles and rankings match what AKC has on file.
            </Text>
          </View>

          {/* Points summary card — shows the live math between historical + runs = total */}
          <View
            style={{
              backgroundColor: "#F0F2F5",
              borderRadius: 10,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <Text style={{ fontSize: 13, color: "#6b7280", fontWeight: "500" }}>Current Total</Text>
            <Text style={{ fontSize: 22, fontWeight: "800", color: "#1B2A4A", marginTop: 2 }}>
              {(+(parseFloat(historicalPoints || "0") + runPoints).toFixed(2)).toString()} pts
            </Text>
            <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              {runPoints.toFixed(2)} from runs + {(parseFloat(historicalPoints || "0") || 0).toFixed(2)} historical
            </Text>
          </View>

          <Input
            label="Historical Points (optional)"
            value={historicalPoints}
            onChangeText={onHistoricalChange}
            placeholder="e.g. 245"
            keyboardType="numeric"
          />
          <Text style={{ fontSize: 12, color: "#6b7280", marginTop: -10, marginBottom: 8 }}>
            Already-earned AKC Fast CAT points before tracking in Dash-Hound. Counts toward title progress and total points only — speed metrics still require logged runs.
          </Text>
          <View style={{ backgroundColor: "#FEE2E2", borderLeftWidth: 4, borderLeftColor: "#DC2626", padding: 10, borderRadius: 6, marginBottom: 14 }}>
            <Text style={{ fontSize: 12, color: "#991B1B", fontWeight: "700" }}>
              Do NOT use this field if you plan on importing or entering each run individually — those runs already add to your total. Using both will double-count your points and incorrectly award titles.
            </Text>
          </View>

          <Input
            label="Total Points (optional)"
            value={totalPoints}
            onChangeText={onTotalChange}
            placeholder="e.g. 295"
            keyboardType="numeric"
          />
          <Text style={{ fontSize: 12, color: "#6b7280", marginTop: -10, marginBottom: pointsWarning ? 6 : 14 }}>
            Type the total your dog has on AKC and we'll back into the historical number for you.
          </Text>
          {pointsWarning ? (
            <View style={{ backgroundColor: "#fef3c7", borderLeftWidth: 3, borderLeftColor: "#f59e0b", padding: 10, borderRadius: 6, marginBottom: 14 }}>
              <Text style={{ fontSize: 12, color: "#92400e" }}>{pointsWarning}</Text>
            </View>
          ) : null}

          <View style={{ marginTop: 8 }}>
            <Button title="Save Changes" onPress={handleSubmit} loading={loading} />
          </View>

          <TouchableOpacity
            onPress={handleDelete}
            style={{
              marginTop: 32,
              borderRadius: 12,
              padding: 14,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              borderWidth: 1,
              borderColor: "#fecaca",
              backgroundColor: "#fef2f2",
            }}
          >
            <Text style={{ color: "#dc2626", fontSize: 14, fontWeight: "600" }}>Delete Dog</Text>
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
